import { createSlice } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import { RootState } from "app/store";
import {
  confirmResetPassword,
  getMe,
  sendResetPasswordEmail,
  signInWithCredentials,
  signOut,
  updatePassword,
} from "app/thunks/authAPI";
import { User } from "interfaces/User";

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticating: boolean;
  isError: boolean;
  hasLoggedIn: boolean;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  isLoading: false,
  isAuthenticating: true,
  isError: false,
  hasLoggedIn: false,
  error: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(signInWithCredentials.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(signInWithCredentials.fulfilled, (state, action) => {
        state.isLoading = false;
        state.hasLoggedIn = action.payload as boolean;
        state.isError = false;
        state.error = null;
      })
      .addCase(signInWithCredentials.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload as string;
      })
      .addCase(updatePassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePassword.fulfilled, (state) => {
        state.isLoading = false;
        state.isError = false;
        state.error = null;
        state.hasLoggedIn = true;
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload as string;
        state.user = null;
      })
      .addCase(sendResetPasswordEmail.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendResetPasswordEmail.fulfilled, (state) => {
        state.isLoading = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(sendResetPasswordEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload as string;
        state.user = null;
      })
      .addCase(confirmResetPassword.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(confirmResetPassword.fulfilled, (state) => {
        state.isLoading = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(confirmResetPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload as string;
        state.user = null;
      })
      .addCase(getMe.pending, (state) => {
        state.isAuthenticating = true;
      })
      .addCase(getMe.fulfilled, (state, action) => {
        state.isAuthenticating = false;
        state.isError = false;
        state.error = null;
        state.user = action.payload as User;
      })
      .addCase(getMe.rejected, (state, action) => {
        state.isAuthenticating = false;
        state.isError = true;
        state.error = action.payload as string;
        state.user = null;
      })
      .addCase(signOut.pending, (state) => {
        state.isLoading = true;
        state.isAuthenticating = true;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticating = false;
        state.isError = true;
        state.error = action.payload as string;
        state.user = null;
      })
      .addCase(resetStore, () => initialState);
  },
});

// ------ Selectors ------

export const getAuthActionState = (state: RootState) => ({
  user: state.auth.user,
  isLoading: state.auth.isLoading,
  isAuthenticating: state.auth.isAuthenticating,
  isError: state.auth.isError,
  error: state.auth.error,
});

export default authSlice.reducer;
